/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {FolioApiService, FolioModel} from './folio-api.service';
import {FolioCopyModalComponent} from './folio-copy-modal.component';
import {FolioItemPageComponent} from './folio-item-page.component';
import {FolioListPageComponent} from './folio-list-page.component';

export function folioStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(FolioApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.manageAdvertising.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiser.apiKey);
  const rh = adnRouterHelper.createRouterHelper('folios.folio', FolioApiService, 'app.adv', 'app');
  const copyConfig = {
    modalComponent: FolioCopyModalComponent,
    stateUrl: 'app.adv.folios.folio',
    lcase: 'folio',
    ucase: 'Folio'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: FolioListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      folioPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<FolioModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true, readOnly(), api, 'Folio', 'name', copyConfig, undefined,
            {objectTypeKey: 'Folio', crumbParams: [{type: 'Advertiser'}]},
            {warningMessage: "The {{action}} of the Folio will also stop any of the Folio's Line Items from serving ads.", object: 'Folio'}
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Folio', 'name', copyConfig, undefined,
          {objectTypeKey: 'Folio', crumbParams: [{type: 'Advertiser'}]},
          {warningMessage: "The {{action}} of the Folio will also stop any of the Folio's Line Items from serving ads.", object: 'Folio'}
        ));
      },
      model: (folioPage: SignalAdminResourcePageState<FolioModel>) => folioPage.model()
    },
    views: {
      'content@app': {component: FolioItemPageComponent, bindings: {state: 'folioPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'folioPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'folioPage'}}
    }
  }));
  return states.build();
}
